/**
 * Wishlist Tracking Utility
 *
 * Sends Klaviyo, GA4, Meta Pixel, and TikTok Pixel events for wishlist actions.
 * Required because the custom UI uses Wishlist Hero's JS SDK directly,
 * and the SDK does not trigger tracking events automatically.
 *
 * Klaviyo events (via Client Events API): "Added To Wishlist (WH)", "Removed From Wishlist (WH)", "Cleared Wishlist (WH)"
 * GA4 event: "add_to_wishlist" (standard event, fired on add only)
 * Meta Pixel event: "AddToWishlist" (standard event, fired on add only)
 * TikTok Pixel event: "AddToWishlist" (standard event, fired on add only)
 */

/**
 * Fetch product data from Shopify for enriching tracking payloads
 * @param {string} productUrl - Product URL containing /products/{handle}
 * @returns {Promise<Object|null>}
 */
async function fetchProductData(productUrl) {
  if (!productUrl) {
    return null;
  }
  const handleMatch = productUrl.match(/\/products\/([^/?#]+)/);
  if (!handleMatch) {
    return null;
  }

  try {
    const response = await fetch(`/products/${handleMatch[1]}.js`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Get current wishlist product IDs from SDK
 * @returns {Promise<number[]>}
 */
async function getWishlistProductIDs() {
  try {
    const items = (await window.WishListHero_SDK?.GetWishListItems()) || [];
    return items.map((item) => parseInt(item.ProductId));
  } catch {
    return [];
  }
}

/**
 * Get locale string for currency code
 * @param {string} currencyCode
 * @returns {string}
 */
function getLocale(currencyCode) {
  const locales = { GBP: 'en-GB', USD: 'en-US', EUR: 'de-DE', CAD: 'en-CA', AUD: 'en-AU' };
  return locales[currencyCode] || 'en-GB';
}

/**
 * Format price string for Klaviyo PriceFormatted field
 * @param {number} price
 * @param {string} currencyCode
 * @returns {string}
 */
function formatPrice(price, currencyCode) {
  try {
    return new Intl.NumberFormat(getLocale(currencyCode), {
      style: 'currency',
      currency: currencyCode,
    }).format(price);
  } catch {
    return `${price}`;
  }
}

/**
 * Build a full URL from a potentially relative path
 * @param {string} url
 * @returns {string}
 */
function buildFullUrl(url) {
  if (!url) {
    return '';
  }
  if (url.startsWith('http')) {
    return url;
  }
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Normalize image URL to include protocol
 * @param {string} imageUrl
 * @returns {string}
 */
function normalizeImageUrl(imageUrl) {
  if (!imageUrl) {
    return '';
  }
  if (imageUrl.startsWith('//')) {
    return `https:${imageUrl}`;
  }
  return imageUrl;
}

/**
 * Get Klaviyo profile attributes for API identification.
 * Tries: 1) logged-in customer email, 2) __kla_id cookie exchange_id/email
 * @returns {Object|null}
 */
function getKlaviyoProfile() {
  if (window.elabsTracking?.customerEmail) {
    return { email: window.elabsTracking.customerEmail };
  }

  try {
    const klaCookie = document.cookie.split('; ').find((c) => c.startsWith('__kla_id='));
    if (klaCookie) {
      const decoded = JSON.parse(atob(klaCookie.split('=')[1]));
      if (decoded.$email) {
        return { email: decoded.$email };
      }
      if (decoded.$exchange_id) {
        return { anonymous_id: decoded.$exchange_id };
      }
      if (decoded.cid) {
        return { anonymous_id: decoded.cid };
      }
    }
  } catch {
    // Cookie parsing failed
  }

  return null;
}

/**
 * Send a Klaviyo event via Client Events API
 * @param {string} eventName
 * @param {Object} payload
 */
function sendKlaviyoEvent(eventName, payload) {
  const companyId = window.elabsTracking?.klaviyoCompanyId;
  if (!companyId) {
    console.warn('Wishlist Tracking: Klaviyo skipped — no companyId', window.elabsTracking);
    return;
  }

  const profileAttributes = getKlaviyoProfile();
  if (!profileAttributes) {
    console.warn('Wishlist Tracking: Klaviyo skipped — no profile found (not logged in, no __kla_id cookie)');
    return;
  }

  try {
    fetch(`https://a.klaviyo.com/client/events/?company_id=${companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2025-04-15',
      },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: {
              data: {
                type: 'metric',
                attributes: { name: eventName },
              },
            },
            properties: payload,
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes,
              },
            },
          },
        },
      }),
    });
  } catch (error) {
    console.error('Wishlist Tracking: Klaviyo API error', error);
  }
}

/**
 * Send a GA4 add_to_wishlist event via gtag
 * @param {Object} eventData
 */
function sendGA4AddToWishlist(eventData) {
  if (typeof window.gtag !== 'function') {
    return;
  }
  try {
    window.gtag('event', 'add_to_wishlist', eventData);
  } catch (error) {
    console.error('Wishlist Tracking: GA4 error', error);
  }
}

/**
 * Send a Meta Pixel AddToWishlist event
 * @param {Object} params
 */
function sendMetaPixelEvent({ productInfo, productData, variant }) {
  if (typeof window.fbq !== 'function') {
    return;
  }
  try {
    const currencyCode = window.Shopify?.currency?.active || 'GBP';
    const price = variant ? variant.price / 100 : parseFloat(productInfo.price) || 0;
    window.fbq('track', 'AddToWishlist', {
      content_name: productInfo.name || productData?.title || '',
      content_ids: [String(productInfo.productId)],
      content_type: 'product',
      value: price,
      currency: currencyCode,
    });
  } catch (error) {
    console.error('Wishlist Tracking: Meta Pixel error', error);
  }
}

/**
 * Send a TikTok Pixel AddToWishlist event
 * @param {Object} params
 */
function sendTikTokEvent({ productInfo, productData, variant }) {
  if (typeof window.ttq?.track !== 'function') {
    return;
  }
  try {
    const currencyCode = window.Shopify?.currency?.active || 'GBP';
    const price = variant ? variant.price / 100 : parseFloat(productInfo.price) || 0;
    window.ttq.track('AddToWishlist', {
      contents: [
        {
          content_id: String(productInfo.productId),
          content_name: productInfo.name || productData?.title || '',
          content_type: 'product',
          price: price,
          quantity: 1,
        },
      ],
      value: price,
      currency: currencyCode,
    });
  } catch (error) {
    console.error('Wishlist Tracking: TikTok Pixel error', error);
  }
}

/**
 * Build Klaviyo-compatible payload for wishlist events
 * @param {Object} params
 * @param {Object} params.productInfo - { productId, variantId, name, price, url, image }
 * @param {Object|null} params.productData - Full Shopify product data
 * @param {Object|null} params.variant - Matched variant from productData
 * @param {number[]} params.wishlistProductIDs - Current wishlist product IDs
 * @returns {Object}
 */
function buildKlaviyoPayload({ productInfo, productData, variant, wishlistProductIDs }) {
  const currencyCode = window.Shopify?.currency?.active || 'GBP';
  const price = variant ? variant.price / 100 : parseFloat(productInfo.price) || 0;

  return {
    Vendor: productData?.vendor || '',
    CurrencyCode: currencyCode,
    ImageURL: normalizeImageUrl(productInfo.image),
    Name: productInfo.name || productData?.title || '',
    Price: price,
    PriceFormatted: formatPrice(price, currencyCode),
    ProductID: parseInt(productInfo.productId),
    VariantID: parseInt(productInfo.variantId),
    Quantity: 1,
    SKU: variant?.sku || '',
    Tags: productData?.tags || [],
    URL: buildFullUrl(productInfo.url),
    WishlistProductIDs: wishlistProductIDs,
  };
}

/**
 * Build GA4 add_to_wishlist event payload
 * @param {Object} params
 * @returns {Object}
 */
function buildGA4Payload({ productInfo, productData, variant }) {
  const currencyCode = window.Shopify?.currency?.active || 'GBP';
  const price = variant ? variant.price / 100 : parseFloat(productInfo.price) || 0;

  return {
    currency: currencyCode,
    value: price,
    items: [
      {
        item_id: String(productInfo.variantId),
        item_name: productInfo.name || productData?.title || '',
        index: 0,
        item_brand: productData?.vendor || '',
        item_category: productData?.type || '',
        item_variant: variant?.title || '',
        price: price,
        quantity: 1,
      },
    ],
  };
}

/**
 * Track "Added To Wishlist (WH)" — fires Klaviyo + GA4 events
 * @param {Object} productInfo - { productId, variantId, name, price, url, image }
 */
export async function trackAddedToWishlist(productInfo) {
  try {
    const productData = await fetchProductData(productInfo.url);
    const variant =
      productData?.variants?.find((v) => String(v.id) === String(productInfo.variantId)) ||
      productData?.variants?.[0] ||
      null;
    const wishlistProductIDs = await getWishlistProductIDs();

    const klaviyoPayload = buildKlaviyoPayload({ productInfo, productData, variant, wishlistProductIDs });
    sendKlaviyoEvent('Added To Wishlist (WH)', klaviyoPayload);

    const ga4Payload = buildGA4Payload({ productInfo, productData, variant });
    sendGA4AddToWishlist(ga4Payload);

    sendMetaPixelEvent({ productInfo, productData, variant });
    sendTikTokEvent({ productInfo, productData, variant });
  } catch (error) {
    console.error('Wishlist Tracking: trackAddedToWishlist error', error);
  }
}

/**
 * Track "Removed From Wishlist (WH)" — fires Klaviyo event only
 * (GA4 does not have a standard remove_from_wishlist event)
 * @param {Object} productInfo - { productId, variantId, name, price, url, image }
 */
export async function trackRemovedFromWishlist(productInfo) {
  try {
    const productData = await fetchProductData(productInfo.url);
    const variant =
      productData?.variants?.find((v) => String(v.id) === String(productInfo.variantId)) ||
      productData?.variants?.[0] ||
      null;
    const wishlistProductIDs = await getWishlistProductIDs();

    const klaviyoPayload = buildKlaviyoPayload({ productInfo, productData, variant, wishlistProductIDs });
    sendKlaviyoEvent('Removed From Wishlist (WH)', klaviyoPayload);
  } catch (error) {
    console.error('Wishlist Tracking: trackRemovedFromWishlist error', error);
  }
}

/**
 * Track "Cleared Wishlist (WH)" — fires Klaviyo event only
 */
export function trackClearedWishlist() {
  try {
    sendKlaviyoEvent('Cleared Wishlist (WH)', {
      WishlistProductIDs: [],
    });
  } catch (error) {
    console.error('Wishlist Tracking: trackClearedWishlist error', error);
  }
}
